import { BankTransaction } from "@akfdev-com/shared/src/models";

/**
 * Derived reconciliation status for a bank transaction (reconciliation Req 11).
 * Never stored — always computed from `reconciled` + `allocatedAmount`:
 *   - matched  → the whole line is explained (`reconciled`)
 *   - partial  → some of the line is allocated but not all (a split in progress)
 *   - needs    → nothing allocated yet
 */
export type ReconStatus = "matched" | "partial" | "needs";

export function deriveReconStatus(tx: BankTransaction): ReconStatus {
  if (tx.reconciled) return "matched";
  if ((tx.allocatedAmount || 0) > 0) return "partial";
  return "needs";
}

export const RECON_STATUS_LABEL: Record<ReconStatus, string> = {
  matched: "Matched",
  partial: "Partly explained",
  needs: "Needs explaining",
};

/**
 * Count transactions by derived status (drives the inbox tab badges).
 */
export function countByStatus(
  transactions: BankTransaction[],
): Record<ReconStatus, number> {
  const counts: Record<ReconStatus, number> = {
    matched: 0,
    partial: 0,
    needs: 0,
  };
  for (const tx of transactions) counts[deriveReconStatus(tx)] += 1;
  return counts;
}

/**
 * Running (closing) balance after each transaction (reconciliation Req 12).
 * Balances are composed in DATE-ASCENDING order — `openingBalance` plus the
 * cumulative signed `grossValue` up to and including each row — regardless of
 * how the table is currently sorted. Same-date transactions keep their input
 * order (stable) so the result is deterministic. Returns a map of
 * `transactionId → running balance` in integer pence; the last row's balance
 * equals the account's current balance.
 */
export function computeRunningBalances(
  transactions: BankTransaction[],
  openingBalance: number,
): Map<string, number> {
  const ordered = transactions
    .map((tx, index) => ({ tx, index }))
    .sort((a, b) => {
      const cmp = a.tx.date.localeCompare(b.tx.date);
      return cmp !== 0 ? cmp : a.index - b.index;
    });

  const balances = new Map<string, number>();
  let running = openingBalance || 0;
  for (const { tx } of ordered) {
    running += tx.grossValue || 0;
    balances.set(tx.id, running);
  }
  return balances;
}
